/**
 * Split Test Results Panel
 *
 * In-builder drawer panel showing one CARD per variant PATH with all metrics
 * and a per-row Promote button. One card per full path, all metrics aligned for
 * scanning, leader highlighted + tagged (never stamped "Winner"). Renders the
 * Promote button and calls the `onPromote(variantId)` callback prop.
 *
 * Mount point: journeys-builder.tsx — an additional <Drawer> alongside the
 * existing node-editor drawer, opened when `panelOpen` is true.
 */

import React, { useMemo } from 'react'
import dayjs from 'dayjs'
import { Alert, Drawer, Skeleton } from 'antd'
import { InfoCircleFilled, TrophyOutlined } from '@ant-design/icons'
import { VariantPathCard } from '@pushly/aqe/lib/components'
import { Journey } from '../types/journey-api-response'
import { useVariantMetrics } from './use-variant-metrics'
import {
    computeLeaderCue,
    getRowsForSplit,
    orderRowsByBranches,
    isLeaderRow,
    shouldShowPurchaseColumns,
    SplitBranchRef,
} from './split-test-metrics'
import './split-test-results-panel.scss'

// Wide enough for the 6-column metric grid (Entrances/Impr/Clicks/CTR/Purch/P.Rate)
// to sit on a single row without wrapping, matching the design.
const DRAWER_WIDTH = 580

// ─── Props ────────────────────────────────────────────────────────────────────

export type SplitTestResultsPanelProps = {
    journey: Journey | null | undefined
    open: boolean
    onClose: () => void
    /** Called when the user clicks Promote for a variant. Receives the variant id. */
    onPromote: (variantId: string | number) => void
    /**
     * When true the journey is a draft and has never run.
     * Promote buttons are disabled; metrics show empty state.
     */
    isDraft?: boolean
    /**
     * When true, promotion is not an available action here (e.g. the panel is
     * opened while viewing a past revision). Promote buttons are HIDDEN entirely,
     * not merely disabled — there is nothing to promote on a historical revision.
     */
    promotionDisabled?: boolean
    /**
     * When set, Promote is DISABLED (not hidden) and this string is shown as the
     * button tooltip — used when the builder has unsaved changes, because promote
     * acts on the saved server graph and would otherwise discard the edits / act on
     * a stale variant set. Takes precedence over the draft / low-data tooltips.
     */
    promoteBlockedReason?: string
    /**
     * The split test this panel is scoped to: its step id (rows are filtered to
     * `split_test.step_id === splitId`) + its branches in canvas/lane order (the
     * drawer lists variants in that order to mirror the canvas — no sort control).
     * A journey can hold several independent split tests; the panel shows exactly
     * one. When null/undefined, falls back to all fetched rows (defensive — every
     * real open path supplies it).
     */
    activeSplit?: { splitId: string | number; branches: SplitBranchRef[] } | null
    getContainer?: () => HTMLElement
}

// ─── Loading skeleton ─────────────────────────────────────────────────────────

function SkeletonCards() {
    return (
        <div className="split-test-results-skeleton" aria-busy="true">
            {[0, 1].map((i) => (
                <div key={i} className="skeleton-card">
                    <Skeleton.Input active size="small" className="skeleton-title" />
                    <Skeleton.Input active size="small" className="skeleton-metrics" />
                </div>
            ))}
        </div>
    )
}

// ─── Component ───────────────────────────────────────────────────────────────

export const SplitTestResultsPanel: React.FC<SplitTestResultsPanelProps> = ({
    journey,
    open,
    onClose,
    onPromote,
    isDraft = false,
    promotionDisabled = false,
    promoteBlockedReason,
    activeSplit,
    getContainer,
}) => {
    const metricsResult = useVariantMetrics(journey, open && !isDraft, 'panel')

    const allRows = metricsResult.status === 'success' ? metricsResult.rows : []
    // Scope to the one split test this panel was opened for (by step id), then
    // order the variants to match the canvas lanes (branch order). A journey can
    // hold several independent split tests; the panel shows exactly one, in the
    // same order as the canvas — so there is no sort control.
    const rows = useMemo(() => {
        if (!activeSplit) return allRows
        const scoped = getRowsForSplit(activeSplit.splitId, allRows)
        return orderRowsByBranches(scoped, activeSplit.branches)
    }, [activeSplit, allRows])
    // Don't show loading spinner for draft journeys — show the empty state directly
    const isLoading = !isDraft && (metricsResult.status === 'loading' || metricsResult.status === 'idle')

    const showPurchases = useMemo(() => shouldShowPurchaseColumns(rows), [rows])
    // On a past/superseded revision the test already concluded — a variant was
    // promoted — so a live "Leading · <metric>" cue is misleading (it can even point
    // at a different variant than the one that was promoted). Suppress it when the
    // panel is opened read-only (promotionDisabled = viewing a past revision); the
    // promoted variant is already marked on the read-only canvas and the Insights
    // Variants report (🏆).
    const leaderCue = useMemo(
        () => (promotionDisabled ? null : computeLeaderCue(rows)),
        [rows, promotionDisabled],
    )

    const startedAt = journey?.created_at ? dayjs(journey.created_at) : null
    const subtitleParts: string[] = []
    if (rows.length > 0) subtitleParts.push(`${rows.length} variants`)
    if (startedAt && startedAt.isValid()) {
        subtitleParts.push(`started ${startedAt.format('MMM D, YYYY')}`)
    }

    // ─── Empty state for draft / no-data journeys ──────────────────────────────
    const renderEmptyState = () => {
        if (isDraft) {
            return (
                <div className="split-test-empty-state">
                    <TrophyOutlined className="empty-state-icon" aria-hidden />
                    <div className="empty-state-title">No data yet</div>
                    <div className="empty-state-desc">
                        Publish this journey to start collecting variant data.
                    </div>
                </div>
            )
        }
        return (
            <div className="split-test-empty-state">
                <TrophyOutlined className="empty-state-icon" aria-hidden />
                <div className="empty-state-title">No variant data</div>
                <div className="empty-state-desc">
                    No split-test variants found for this journey.
                </div>
            </div>
        )
    }

    const renderBody = () => {
        if (isLoading) return <SkeletonCards />
        if (rows.length === 0) return renderEmptyState()

        return (
            <>
                {/* Unsaved-changes callout: far more discoverable than the per-button
                    tooltip (which needs a hover-and-wait on a disabled button). Promote
                    acts on the SAVED journey, so we surface the blocker up front and keep
                    the per-card Promote buttons disabled underneath. */}
                {promoteBlockedReason && (
                    <div role="alert">
                        <Alert
                            className="split-test-results-dirty-callout"
                            type="warning"
                            showIcon
                            title="Save your changes to promote"
                            description="Promotion applies to the saved version of this journey. Save your changes in the builder first, then promote a variant."
                            style={{ marginBottom: 12 }}
                        />
                    </div>
                )}

                <div className="results-controls">
                    <span className="results-controls-label">Variant paths</span>
                </div>

                <div className="variant-path-cards">
                    {rows.map((row) => {
                        // Promotion semantics are app-glue and stay here (the AQE card owns
                        // no draft logic): disable + explain why. The impression floor only
                        // affects the leader cue + rate display, never the ability to promote.
                        // Unsaved-changes block takes precedence: promote runs on the saved
                        // server graph, so it must be blocked until the builder is saved.
                        const promoteTooltip = promoteBlockedReason
                            ? promoteBlockedReason
                            : isDraft
                            ? 'Journey must be published before promoting a variant'
                            : 'Promote this variant'
                        // The metrics row's variantId IS the branch key (a stable nanoid),
                        // so it's the promote key directly.
                        const promoteKey = row.variantId
                        return (
                            <VariantPathCard
                                key={String(row.variantId) + '::' + row.variantName}
                                variant={row}
                                isLeader={isLeaderRow(leaderCue, row)}
                                leaderMetric={leaderCue?.metric ?? null}
                                showPurchaseRate={showPurchases}
                                promoteDisabled={isDraft || !!promoteBlockedReason}
                                promoteTooltip={promoteTooltip}
                                hidePromote={promotionDisabled}
                                onPromote={() => onPromote(promoteKey)}
                            />
                        )
                    })}
                </div>
            </>
        )
    }

    return (
        <Drawer
            className="split-test-results-panel"
            title={
                <div className="results-title">
                    <span className="results-title-main">Split Test Results</span>
                    {subtitleParts.length > 0 && (
                        <span className="results-title-sub">{subtitleParts.join(' · ')}</span>
                    )}
                </div>
            }
            placement="right"
            open={open}
            onClose={onClose}
            width={DRAWER_WIDTH}
            destroyOnHidden={true}
            getContainer={getContainer}
            footer={
                <div className="panel-footer-note">
                    Promoting a variant ends the test, makes its path the journey's only flow,
                    and can't be undone. Subscribers in other paths finish where they are.
                </div>
            }
        >
            <div className="results-helper-note">
                <InfoCircleFilled className="results-helper-icon" aria-hidden />
                <span>
                    Each row totals the <strong>full path</strong> — every step on that branch
                    combined, not a single notification.
                </span>
            </div>
            {renderBody()}
        </Drawer>
    )
}
